import type { UpdateAssistantInput } from '@crowdex/agent-contracts/api';
import { useAvatars } from '@/features/workspaces/workspace-resources';
import { useAccountAgentProfiles } from '@/hooks/use-account-agent-profiles';
import { useMediaQuery } from '@/hooks/use-media-query';
import { openAppHome } from '@/lib/app-home';
import {
  isDesktopPreviewPath,
  useWorkspaceDirectory,
  workspaceDirectoryStore,
} from '@/lib/assistants/directory';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { PREVIEW_PERSONAS } from './assistant-preview-data';
import { AssistantSettingsTab } from './assistant-settings-tab';
import { AssistantTasksTab } from './assistant-tasks-tab';
import {
  refreshAssistant,
  useAssistantOfConversation,
  useAssistantPersonas,
  useAssistantState,
} from './use-assistant';

/**
 * An assistant's channel's panel tab (D65 Tasks, D67 settings), or on a phone its page (mobile
 * 2e: every section).
 */
export function AssistantPanel({
  view,
  conversationId,
}: {
  view: 'project-tasks' | 'project';
  conversationId: string;
}) {
  const project = useAssistantOfConversation(conversationId);
  const projects = useWorkspaceDirectory((state) => state.projects);
  const avatarList = useAvatars();
  const { overview, tasks } = useAssistantState(project);
  const personas = useAssistantPersonas(project);
  const opener = useConversationOpener();
  const store = useDesktopShellStoreApi();
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  const { profiles } = useAccountAgentProfiles();
  // The saved agents the assistant may hand work to: the user's (the boards' in the preview).
  const savedAgents = isDesktopPreviewPath()
    ? [
        ...PREVIEW_PERSONAS.map((persona) => ({ id: persona.agentId, name: persona.name })),
        { id: 'writer', name: 'Writer' },
      ]
    : profiles.map((profile) => ({ id: profile.id, name: profile.name }));
  if (project === null)
    return (
      <p className="type-meta p-4 text-muted-foreground">
        This conversation is not an assistant's.
      </p>
    );
  const open = (id: string) => void opener?.openConversation(id);
  const update = async (input: UpdateAssistantInput) => {
    await workspaceDirectoryStore().getState().updateAssistant(project.id, input);
    await refreshAssistant(project.id);
  };
  if (view === 'project-tasks')
    return (
      <AssistantTasksTab automations={overview?.automations ?? []} onOpen={open} tasks={tasks} />
    );
  // Each avatar says which assistant has it: choosing it here takes it from that one.
  const avatars = avatarList.data.map((avatar) => ({
    ...avatar,
    usedBy:
      projects.find((entry) => entry.avatarId === avatar.id && entry.archivedAt === null)?.name ??
      null,
  }));
  const provider = overview?.automations?.find((entry) => entry.provider !== undefined)?.provider;
  return (
    <AssistantSettingsTab
      avatars={avatars}
      avatarsKnown={avatarList.status === 'ready'}
      onConversation={() => store.getState().setSidePanelOpen(false)}
      automations={overview?.automations ?? []}
      {...(provider === undefined
        ? {}
        : {
            // In the app of the plugin that runs them, which edits them.
            onOpenAutomation: (automation?: { id: string; provider?: string | undefined }) =>
              openAppHome(automation?.provider ?? provider, automation?.id ?? null),
          })}
      onNewTask={() => store.getState().setSidePanelOpen(false)}
      onOpenTask={open}
      onUpdate={update}
      personas={personas}
      project={project}
      repositories={overview?.repositories ?? []}
      savedAgents={savedAgents}
      tasks={tasks}
      variant={phone ? 'page' : 'tab'}
    />
  );
}
